import { useState, useEffect, useMemo, useRef } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import {
  ClipboardList, Plus, RefreshCw, FileText, CheckCircle, Clock, AlertCircle,
  DollarSign, BookOpen, Upload, Trash2, Download, FileAudio, FileVideo,
  Loader2, Eye, File, ImageIcon, Presentation,
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { Badge } from '@/components/ui/badge';

interface ProcedureType {
  id: number;
  code: string;
  name: string;
  description: string | null;
  hasCost: boolean;
  product: { name: string; price: string } | null;
}

interface Procedure {
  id: number;
  status: string;
  notes: string | null;
  requestedAt: string;
  completedAt: string | null;
  procedureType: { id: number; code: string; name: string };
  hasCost: boolean;
  productPrice: string | null;
}

interface CourseCombo {
  courseId: number;
  courseName: string;
  courseCode: string;
  periodId: number;
  periodName: string;
  periodCode: string;
  programName: string;
  materialCount: number;
}

interface Material {
  id: number;
  titulo: string;
  descripcion: string | null;
  archivoNombre: string;
  archivoSize: number;
  archivoTipo: string;
  createdAt: string;
  uploadedBy: string;
}

const statusLabels: Record<string, { label: string; color: string; icon: any }> = {
  'solicitado': { label: 'Solicitado', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  'pendiente_pago': { label: 'Pendiente de Pago', color: 'bg-red-100 text-red-800', icon: DollarSign },
  'pagado_en_proceso': { label: 'Pagado - En Proceso', color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
  'en_proceso': { label: 'En Proceso', color: 'bg-blue-100 text-blue-800', icon: Clock },
  'completado': { label: 'Completado', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  'cancelado': { label: 'Cancelado', color: 'bg-gray-100 text-gray-800', icon: AlertCircle },
};

function getFileIcon(mimeType: string) {
  if (mimeType?.startsWith('image/')) return <ImageIcon className="h-5 w-5 text-blue-500" />;
  if (mimeType?.startsWith('audio/')) return <FileAudio className="h-5 w-5 text-green-500" />;
  if (mimeType?.startsWith('video/')) return <FileVideo className="h-5 w-5 text-purple-500" />;
  if (mimeType === 'application/pdf') return <FileText className="h-5 w-5 text-red-500" />;
  if (mimeType?.includes('word')) return <FileText className="h-5 w-5 text-blue-700" />;
  if (mimeType?.includes('excel') || mimeType?.includes('spreadsheet')) return <FileText className="h-5 w-5 text-green-700" />;
  if (mimeType?.includes('powerpoint') || mimeType?.includes('presentation')) return <Presentation className="h-5 w-5 text-orange-500" />;
  return <File className="h-5 w-5 text-gray-500" />;
}

function formatFileSize(bytes: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isPreviewable(mimeType: string): boolean {
  return /^(image\/|audio\/|video\/|application\/pdf)/.test(mimeType || '');
}

export default function TeacherProcedures() {
  const { toast } = useToast();

  // ── Procedures (Mis Trámites) ───────────────────────────────────────────────
  const [procedures, setProcedures] = useState<Procedure[]>([]);
  const [procedureTypes, setProcedureTypes] = useState<ProcedureType[]>([]);
  const [loading, setLoading] = useState(true);
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [formData, setFormData] = useState({ procedureTypeId: '', notes: '' });
  const [selectedType, setSelectedType] = useState<ProcedureType | null>(null);

  // ── Materials (Material de Clases) ─────────────────────────────────────────
  const [courses, setCourses] = useState<CourseCombo[]>([]);
  const [coursesLoaded, setCoursesLoaded] = useState(false);
  const [selectedPeriodId, setSelectedPeriodId] = useState('');
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [materials, setMaterials] = useState<Material[]>([]);
  const [materialsLoading, setMaterialsLoading] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadTitulo, setUploadTitulo] = useState('');
  const [uploadDescripcion, setUploadDescripcion] = useState('');
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Derived selectors ───────────────────────────────────────────────────────
  const periods = useMemo(() => {
    const seen = new Map<number, { periodId: number; periodName: string; periodCode: string }>();
    courses.forEach(c => {
      if (!seen.has(c.periodId)) seen.set(c.periodId, { periodId: c.periodId, periodName: c.periodName, periodCode: c.periodCode });
    });
    return Array.from(seen.values()).sort((a, b) => a.periodName.localeCompare(b.periodName));
  }, [courses]);

  const coursesForPeriod = useMemo(() => {
    if (!selectedPeriodId) return [];
    return courses.filter(c => c.periodId.toString() === selectedPeriodId);
  }, [courses, selectedPeriodId]);

  const selectedCombo = useMemo(() =>
    courses.find(c => c.courseId.toString() === selectedCourseId && c.periodId.toString() === selectedPeriodId) ?? null,
    [courses, selectedCourseId, selectedPeriodId]
  );

  // ── Data fetching ───────────────────────────────────────────────────────────
  const fetchMyProcedures = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/procedures/my-teacher-procedures', { credentials: 'include' });
      if (res.ok) setProcedures(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchProcedureTypes = async () => {
    try {
      const res = await fetch('/api/procedures/types/active?audience=profesor', { credentials: 'include' });
      if (res.ok) setProcedureTypes(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchMyCourses = async () => {
    if (coursesLoaded) return;
    try {
      const res = await fetch('/api/teacher-agenda/materials/my-courses', { credentials: 'include' });
      if (res.ok) {
        setCourses(await res.json());
        setCoursesLoaded(true);
      }
    } catch (e) { console.error(e); }
  };

  const fetchMaterials = async (courseId: string, periodId: string) => {
    if (!courseId || !periodId) return;
    setMaterialsLoading(true);
    try {
      const res = await fetch(`/api/teacher-agenda/materials?courseId=${courseId}&periodId=${periodId}`, { credentials: 'include' });
      if (res.ok) setMaterials(await res.json());
    } catch (e) { console.error(e); }
    finally { setMaterialsLoading(false); }
  };

  useEffect(() => { fetchMyProcedures(); fetchProcedureTypes(); }, []);

  useEffect(() => {
    if (selectedCourseId && selectedPeriodId) fetchMaterials(selectedCourseId, selectedPeriodId);
    else setMaterials([]);
  }, [selectedCourseId, selectedPeriodId]);

  // ── Procedures handlers ─────────────────────────────────────────────────────
  const handleTypeChange = (typeId: string) => {
    setFormData({ ...formData, procedureTypeId: typeId });
    setSelectedType(procedureTypes.find(t => t.id.toString() === typeId) || null);
  };

  const handleRequest = async () => {
    if (!formData.procedureTypeId) {
      toast({ title: 'Error', description: 'Seleccione un tipo de trámite', variant: 'destructive' });
      return;
    }
    try {
      const res = await fetch('/api/procedures/teacher-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ procedureTypeId: parseInt(formData.procedureTypeId), notes: formData.notes || undefined }),
      });
      if (res.ok) {
        toast({ title: 'Trámite solicitado', description: 'Tu solicitud ha sido registrada exitosamente.' });
        setShowRequestDialog(false);
        setFormData({ procedureTypeId: '', notes: '' });
        setSelectedType(null);
        fetchMyProcedures();
      } else {
        const err = await res.json();
        toast({ title: 'Error', description: err.error || 'Error al solicitar trámite', variant: 'destructive' });
      }
    } catch { toast({ title: 'Error', description: 'Error de conexión', variant: 'destructive' }); }
  };

  // ── Materials handlers ──────────────────────────────────────────────────────
  const handlePeriodChange = (periodId: string) => {
    setSelectedPeriodId(periodId);
    setSelectedCourseId('');
    setMaterials([]);
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploadTitulo.trim() || !selectedCourseId || !selectedPeriodId) {
      toast({ title: 'Error', description: 'Completa título, grupo, materia y selecciona un archivo', variant: 'destructive' });
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('archivo', uploadFile);
      fd.append('titulo', uploadTitulo.trim());
      fd.append('descripcion', uploadDescripcion.trim());
      fd.append('courseId', selectedCourseId);
      fd.append('periodId', selectedPeriodId);

      const res = await fetch('/api/teacher-agenda/materials', { method: 'POST', credentials: 'include', body: fd });
      if (res.ok) {
        toast({ title: 'Archivo subido', description: `"${uploadFile.name}" subido correctamente.` });
        setUploadFile(null);
        setUploadTitulo('');
        setUploadDescripcion('');
        if (fileInputRef.current) fileInputRef.current.value = '';
        fetchMaterials(selectedCourseId, selectedPeriodId);
      } else {
        const err = await res.json();
        toast({ title: 'Error', description: err.error || 'Error al subir archivo', variant: 'destructive' });
      }
    } catch { toast({ title: 'Error', description: 'Error de conexión', variant: 'destructive' }); }
    finally { setUploading(false); }
  };

  const handleDelete = async (id: number) => {
    setDeletingId(id);
    try {
      const res = await fetch(`/api/teacher-agenda/materials/${id}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) {
        setMaterials(prev => prev.filter(m => m.id !== id));
        toast({ title: 'Eliminado', description: 'Material eliminado.' });
      } else {
        toast({ title: 'Error', description: 'No se pudo eliminar', variant: 'destructive' });
      }
    } catch { toast({ title: 'Error', description: 'Error de conexión', variant: 'destructive' }); }
    finally { setDeletingId(null); }
  };

  const handleDownload = async (material: Material) => {
    try {
      const res = await fetch(`/api/teacher-agenda/materials/${material.id}/download`, { credentials: 'include' });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = material.archivoNombre;
        document.body.appendChild(a); a.click();
        window.URL.revokeObjectURL(url); document.body.removeChild(a);
      }
    } catch { toast({ title: 'Error', description: 'No se pudo descargar', variant: 'destructive' }); }
  };

  const formatDate = (d: string) => new Date(d).toLocaleDateString('es-MX', { year: 'numeric', month: 'short', day: 'numeric' });
  const formatCurrency = (a: string) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(parseFloat(a));

  return (
    <div className="p-6 space-y-6">
      {/* ── Header ── */}
      <div className="flex items-center gap-3">
        <BookOpen className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">Servicios Docentes</h1>
          <p className="text-muted-foreground">Gestiona tus trámites y materiales de clase</p>
        </div>
      </div>

      {/* ── Tabs ── */}
      <Tabs defaultValue="tramites">
        <TabsList className="mb-4">
          <TabsTrigger value="tramites">
            <ClipboardList className="h-4 w-4 mr-2" />
            Mis Trámites
          </TabsTrigger>
          <TabsTrigger value="materiales" onClick={fetchMyCourses}>
            <BookOpen className="h-4 w-4 mr-2" />
            Material de Clases
          </TabsTrigger>
        </TabsList>

        {/* ══════════════════ TAB: Mis Trámites ══════════════════ */}
        <TabsContent value="tramites">
          <div className="space-y-4">
            <div className="flex justify-end gap-2">
              <Button onClick={fetchMyProcedures} variant="outline" size="sm">
                <RefreshCw className="h-4 w-4 mr-2" />Actualizar
              </Button>
              <Button onClick={() => setShowRequestDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />Solicitar Trámite
              </Button>
            </div>

            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
            ) : procedures.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No tienes trámites registrados</p>
                  <p className="text-sm mt-2">Haz clic en "Solicitar Trámite" para comenzar</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {procedures.map((procedure) => {
                  const statusInfo = statusLabels[procedure.status] || statusLabels['solicitado'];
                  const StatusIcon = statusInfo.icon;
                  return (
                    <Card key={procedure.id}>
                      <CardContent className="py-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-4">
                            <div className={`p-2 rounded-full ${statusInfo.color}`}>
                              <StatusIcon className="h-5 w-5" />
                            </div>
                            <div>
                              <h3 className="font-semibold">{procedure.procedureType.name}</h3>
                              <p className="text-sm text-muted-foreground">Solicitado el {formatDate(procedure.requestedAt)}</p>
                              {procedure.notes && <p className="text-sm mt-1">{procedure.notes}</p>}
                            </div>
                          </div>
                          <div className="text-right">
                            <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
                              {statusInfo.label}
                            </span>
                            {procedure.completedAt && (
                              <p className="text-xs text-green-600 mt-1">Completado el {formatDate(procedure.completedAt)}</p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>

          {/* Request Dialog */}
          <Dialog open={showRequestDialog} onOpenChange={setShowRequestDialog}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Solicitar Trámite</DialogTitle>
                <DialogDescription>Selecciona el tipo de trámite que necesitas</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Tipo de Trámite *</Label>
                  <Select value={formData.procedureTypeId} onValueChange={handleTypeChange}>
                    <SelectTrigger><SelectValue placeholder="Seleccionar trámite" /></SelectTrigger>
                    <SelectContent>
                      {procedureTypes.map(t => (
                        <SelectItem key={t.id} value={t.id.toString()}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {selectedType && (
                  <div className="p-3 bg-muted rounded-lg space-y-2">
                    {selectedType.description && <p className="text-sm">{selectedType.description}</p>}
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4" />
                      <span className="text-sm font-medium">
                        {selectedType.hasCost && selectedType.product
                          ? `Costo: ${formatCurrency(selectedType.product.price)}`
                          : 'Sin costo'}
                      </span>
                    </div>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Notas adicionales (opcional)</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={e => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Información adicional..."
                    rows={3}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowRequestDialog(false)}>Cancelar</Button>
                <Button onClick={handleRequest}>Solicitar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* ══════════════════ TAB: Material de Clases ══════════════════ */}
        <TabsContent value="materiales">
          <div className="space-y-6">
            {/* ── Step 1 & 2: Selectors ── */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Selecciona Grupo y Materia</CardTitle>
                <CardDescription>Elige el grupo y la materia para gestionar sus materiales</CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Grupo *</Label>
                  {periods.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-2">No tienes grupos asignados</p>
                  ) : (
                    <Select value={selectedPeriodId} onValueChange={handlePeriodChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar grupo…" />
                      </SelectTrigger>
                      <SelectContent>
                        {periods.map(p => (
                          <SelectItem key={p.periodId} value={p.periodId.toString()}>
                            {p.periodName} {p.periodCode ? `(${p.periodCode})` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Materia *</Label>
                  <Select
                    value={selectedCourseId}
                    onValueChange={setSelectedCourseId}
                    disabled={!selectedPeriodId || coursesForPeriod.length === 0}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={selectedPeriodId ? 'Seleccionar materia…' : 'Primero selecciona un grupo'} />
                    </SelectTrigger>
                    <SelectContent>
                      {coursesForPeriod.map(c => (
                        <SelectItem key={c.courseId} value={c.courseId.toString()}>
                          {c.courseName} {c.courseCode ? `(${c.courseCode})` : ''}
                          {c.materialCount > 0 && (
                            <span className="ml-2 text-xs text-muted-foreground">· {c.materialCount} archivo{c.materialCount !== 1 ? 's' : ''}</span>
                          )}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* ── Upload form (only when course is selected) ── */}
            {selectedCourseId && selectedPeriodId && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Upload className="h-4 w-4" />
                    Subir Archivo
                  </CardTitle>
                  <CardDescription>
                    Soporta PDF, Word, Excel, PowerPoint, imágenes, audio y video (máx. 50 MB)
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Título *</Label>
                    <Input
                      value={uploadTitulo}
                      onChange={e => setUploadTitulo(e.target.value)}
                      placeholder="Nombre descriptivo del material"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Descripción (opcional)</Label>
                    <Textarea
                      value={uploadDescripcion}
                      onChange={e => setUploadDescripcion(e.target.value)}
                      placeholder="Descripción o instrucciones..."
                      rows={2}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Archivo *</Label>
                    <Input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif,.mp4,.mp3,.wav,.avi,.mov"
                      onChange={e => setUploadFile(e.target.files?.[0] ?? null)}
                    />
                    {uploadFile && (
                      <p className="text-xs text-muted-foreground">
                        {uploadFile.name} · {formatFileSize(uploadFile.size)}
                      </p>
                    )}
                  </div>
                  <Button onClick={handleUpload} disabled={uploading || !uploadFile || !uploadTitulo.trim()}>
                    {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                    {uploading ? 'Subiendo…' : 'Subir Archivo'}
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* ── Materials list ── */}
            {selectedCourseId && selectedPeriodId && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Materiales Subidos
                    {selectedCombo && (
                      <Badge variant="secondary" className="ml-1">
                        {selectedCombo.courseName} · {selectedCombo.periodName}
                      </Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {materialsLoading ? (
                    <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
                  ) : materials.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <FileText className="h-10 w-10 mx-auto mb-3 opacity-40" />
                      <p>Aún no hay materiales para esta materia</p>
                      <p className="text-sm">Sube el primer archivo usando el formulario de arriba</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {materials.map(m => (
                        <div key={m.id} className="flex items-center justify-between gap-3 border rounded-lg p-3">
                          <div className="flex items-center gap-3 min-w-0">
                            {getFileIcon(m.archivoTipo)}
                            <div className="min-w-0">
                              <p className="font-medium text-sm truncate">{m.titulo}</p>
                              <p className="text-xs text-muted-foreground truncate">
                                {m.archivoNombre} · {formatFileSize(m.archivoSize)} · {formatDate(m.createdAt)}
                              </p>
                              {m.descripcion && <p className="text-xs text-muted-foreground mt-0.5">{m.descripcion}</p>}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Button size="sm" variant="outline" onClick={() => handleDownload(m)} title="Descargar">
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-destructive hover:bg-destructive/10"
                              onClick={() => handleDelete(m.id)}
                              disabled={deletingId === m.id}
                              title="Eliminar"
                            >
                              {deletingId === m.id
                                ? <Loader2 className="h-4 w-4 animate-spin" />
                                : <Trash2 className="h-4 w-4" />}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {!selectedPeriodId && periods.length > 0 && (
              <div className="text-center py-12 text-muted-foreground">
                <BookOpen className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>Selecciona un grupo para comenzar</p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
